import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

/**
 * MediaRecorder는 브라우저별로 mp4 직접 녹화를 지원하지 않는 경우가 많아(Chrome/Edge 등),
 * 항상 webm으로 녹화한 뒤 ffmpeg.wasm으로 mp4 컨테이너로 재인코딩한다.
 * 코어(wasm)는 최초 변환 시 1회만 CDN에서 내려받아 캐싱된다.
 */
const CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

let ffmpegInstance = null;
let loadPromise = null;
let currentProgressHandler = null;

async function getFFmpeg() {
  if (ffmpegInstance) return ffmpegInstance;

  if (!loadPromise) {
    const ffmpeg = new FFmpeg();
    ffmpeg.on('progress', ({ progress }) => {
      if (typeof currentProgressHandler === 'function' && Number.isFinite(progress)) {
        currentProgressHandler(Math.min(1, Math.max(0, progress)));
      }
    });

    loadPromise = Promise.all([
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
    ])
      .then(([coreURL, wasmURL]) => ffmpeg.load({ coreURL, wasmURL }))
      .then(() => {
        ffmpegInstance = ffmpeg;
        return ffmpeg;
      })
      .catch((err) => {
        loadPromise = null; // 실패 시 다음 시도에서 재로드할 수 있도록 초기화
        throw err;
      });
  }

  return loadPromise;
}

/**
 * webm(Blob)을 mp4(Blob)로 변환합니다.
 * @param {Blob} webmBlob
 * @param {(ratio: number) => void} [onProgress] - 0~1 진행률 콜백
 * @returns {Promise<Blob>}
 */
export async function convertWebmToMp4(webmBlob, onProgress) {
  const ffmpeg = await getFFmpeg();
  currentProgressHandler = onProgress || null;

  const inputName = `input-${Date.now()}.webm`;
  const outputName = `output-${Date.now()}.mp4`;

  try {
    await ffmpeg.writeFile(inputName, await fetchFile(webmBlob));
    await ffmpeg.exec([
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'veryfast',
      '-crf', '23',
      '-pix_fmt', 'yuv420p',
      '-c:a', 'aac',
      outputName,
    ]);
    const data = await ffmpeg.readFile(outputName);
    return new Blob([data.buffer], { type: 'video/mp4' });
  } finally {
    currentProgressHandler = null;
    await ffmpeg.deleteFile(inputName).catch(() => {});
    await ffmpeg.deleteFile(outputName).catch(() => {});
  }
}
